import { useState, useCallback, useMemo } from 'react';
import { downloadBlob } from '@chikara/utils';
import { Button, useToast } from '@chikara/ui';
import type { FilterTabItem } from '@chikara/ui';
import { PageContent } from '@/shared/components/PageContent';
import { UserModal } from '@/features/team/components/UserModals';
import { TeamTable } from '@/features/team/components/TeamTable/TeamTable';
import { TeamFilters } from '@/features/team/components/TeamFilters/TeamFilters';
import { useTeam } from '@/features/team/hooks/useTeam/useTeam';
import { teamService } from '@/features/team/services/teamService/teamService';
import { buildTeamListFilter, hasAnyTeamFilter } from '@/features/team/utils/teamFilters.utils';
import { buildTeamExportDirective, TEAM_PDF_FILENAME, TEAM_XLSX_FILENAME } from '@/features/team/utils/teamExport.utils';
import type { ExportFormat } from '@/shared/export/types';
import { buildExportIdentity } from '@/shared/export/identity';

import { useAuth } from '@/features/auth/hooks/useAuth';
import { TEAM_ALL_VALUE, TEAM_ROLE_FILTERS, TEAM_STATUS_FILTERS, teamStatusLabel } from '@/features/team/constants/team.constants';
import type { AddUserFormValues, TeamFilters as TeamFiltersState } from '@/features/team/types/team.types';
import { t } from '@/i18n';
import styles from './TeamPage.module.css';

export function TeamPage() {
  const toast = useToast();
  const { activeCompany, payload } = useAuth();

  // Filtros: borrador (controles) y aplicado (consulta actual).
  const [draftFilters, setDraftFilters] = useState<TeamFiltersState>({});
  const [appliedFilters, setAppliedFilters] = useState<TeamFiltersState>({});

  const {
    members,
    pagination,
    isLoading,
    createMember,
    resendInvitation,
    approveMember,
    updateMember,
    cancelMember,
    getMember,
    catalogs,
    counts,
  } = useTeam(appliedFilters);

  const [addUserModalOpen, setAddUserModalOpen] = useState(false);

  // Catálogos de filtro: roles/estatus del BE (con fallback a las constantes
  // mientras el catálogo no esté desplegado).
  const tabs = useMemo<FilterTabItem[]>(() => {
    const withCount = (label: string, count?: number) =>
      typeof count === 'number' ? `${label} (${count})` : label;

    return [
      { value: TEAM_ALL_VALUE, label: withCount(t('team.tabs.all'), counts.all) },
      ...TEAM_STATUS_FILTERS.map((status) => ({
        value: status.value,
        label: withCount(t(status.labelKey), counts[status.value]),
      })),
    ];
  }, [counts]);

  const statusOptions = useMemo(() => {
    const values = catalogs.statuses.length > 0
      ? catalogs.statuses
      : TEAM_STATUS_FILTERS.map((status) => status.value);
    return values.map((value) => ({ value, label: teamStatusLabel(value) }));
  }, [catalogs.statuses]);

  const roleOptions = useMemo(() => {
    const values = catalogs.roles.length > 0
      ? catalogs.roles
      : TEAM_ROLE_FILTERS.map((role) => role.value);
    const labels: Record<string, string> = {
      OWNER: 'team.filters.role.owner',
      ADMIN: 'team.filters.role.admin',
      BASIC: 'team.filters.role.basic',
    };
    return values.map((value) => ({ value, label: labels[value] ? t(labels[value]) : value }));
  }, [catalogs.roles]);

  const handleFilterChange = useCallback((patch: Partial<TeamFiltersState>) => {
    setDraftFilters((prev) => ({ ...prev, ...patch }));
  }, []);

  // La pestaña aplica de inmediato: mueve borrador y aplicados a la vez.
  const handleStatusTabChange = useCallback((value: string) => {
    const status = value === TEAM_ALL_VALUE ? undefined : value;
    setDraftFilters((prev) => ({ ...prev, status }));
    setAppliedFilters((prev) => ({ ...prev, status }));
  }, []);

  const handleApplyFilters = useCallback(() => {
    setAppliedFilters(draftFilters);
  }, [draftFilters]);

  const handleClearFilters = useCallback(() => {
    setDraftFilters({});
    setAppliedFilters({});
  }, []);

  const buildTeamDirective = (format: ExportFormat) =>
    buildTeamExportDirective(
      {
        filters: appliedFilters,
        ...buildExportIdentity({
          companyName: activeCompany?.company_name,
          keyId: payload?.customer_id,
          customerUuid: payload?.customer_uuid ?? activeCompany?.customer_uuid,
        }),
      },
      format
    );

  const buildTeamExportRequest = () => ({
    page_number: 1,
    results_per_page: pagination.totalItems,
    ...buildTeamListFilter(appliedFilters),
  });

  const handleExportXlsx = useCallback(async () => {
    try {
      const { blob, filename } = await teamService.exportTeam(
        buildTeamExportRequest(),
        buildTeamDirective('xlsx')
      );
      downloadBlob(blob, filename ?? TEAM_XLSX_FILENAME);
    } catch {
      toast.error(t('export.errors.download'));
    }
  }, [appliedFilters, pagination.totalItems, activeCompany, payload, toast]);

  const handleExportPdf = useCallback(async () => {
    try {
      const { blob, filename } = await teamService.exportTeam(
        buildTeamExportRequest(),
        buildTeamDirective('pdf')
      );
      downloadBlob(blob, filename ?? TEAM_PDF_FILENAME);
    } catch {
      toast.error(t('export.errors.download'));
    }
  }, [appliedFilters, pagination.totalItems, activeCompany, payload, toast]);

  const handleAddUser = useCallback(() => {
    setAddUserModalOpen(true);
  }, []);

  const handleAddUserSubmit = useCallback(async (values: AddUserFormValues) => {
    await createMember(values);
    setAddUserModalOpen(false);
  }, [createMember]);

  return (
    <PageContent 
      title={t('team.title')}
      button={
        <Button variant="primary" size="md" onClick={handleAddUser}>
          {t('team.modals.addUser.submit')}
        </Button>
      }
    > 
      <section className={styles.section}>
        <TeamFilters
          tabs={tabs}
          roleOptions={roleOptions}
          statusOptions={statusOptions}
          draftFilters={draftFilters}
          onFilterChange={handleFilterChange}
          activeStatus={appliedFilters.status ?? TEAM_ALL_VALUE}
          onStatusTabChange={handleStatusTabChange}
          onApply={handleApplyFilters}
          onClear={handleClearFilters}
          isApplying={isLoading}
          onExportExcel={handleExportXlsx}
          onExportPdf={handleExportPdf}
          hasResults={pagination.totalItems > 0}
        />

        <TeamTable
          members={members}
          isLoading={isLoading}
          hasActiveFilters={hasAnyTeamFilter(appliedFilters)}
          pagination={pagination}
          onApprove={approveMember}
          onUpdate={updateMember}
          onDeactivate={cancelMember}
          onGetMember={getMember}
          onResendInvitation={resendInvitation}
        />

        <UserModal
          mode="add"
          isOpen={addUserModalOpen}
          onClose={() => setAddUserModalOpen(false)}
          onSubmit={handleAddUserSubmit}
        />
      </section>
    </PageContent>
  );
}

export default TeamPage;
